import { trackingOptedOut } from "src/utils/consent";

export const MetaPixelId = "3076719802571656";

const PixelScriptUrl = "https://connect.facebook.net/en_US/fbevents.js";

export function fbq(...args) {
  if ("undefined" === typeof window) return;
  const fbq = (window as any).fbq;
  if ("undefined" === typeof fbq) return;
  return fbq(...args);
}

/** Meta's standard base code: a queueing stub plus the async library. */
function loadPixel() {
  const w = window as any;
  if (w.fbq) return;

  const stub: any = function (...args: unknown[]) {
    if (stub.callMethod) stub.callMethod(...args);
    else stub.queue.push(args);
  };
  stub.push = stub;
  stub.loaded = true;
  stub.version = "2.0";
  stub.queue = [];
  w.fbq = stub;
  if (!w._fbq) w._fbq = stub;

  const script = document.createElement("script");
  script.async = true;
  script.src = PixelScriptUrl;
  document.head.appendChild(script);

  stub("init", MetaPixelId);
  stub("track", "PageView");
}

export interface ProspectSession {
  pathname: string;
  authorizing: boolean;
  loggedIn: boolean;
  user?: { plan?: string | null } | null;
  /** Support / accountant impersonation of a client account. */
  impersonating: boolean;
  /** Support or accountant portal login in this browser. */
  supportLoggedIn: boolean;
}

export interface MetaPixelSession extends ProspectSession {
  hostname: string;
}

/**
 * Whether this is a prospect: a visitor on the public pages, or a signed-up
 * user who hasn't subscribed yet. Subscribers, support staff and accountants
 * are not. `undefined` means "not known yet" (auth still restoring a session).
 * A just-logged-in user whose user doc hasn't loaded counts as not a prospect
 * until it shows whether they subscribed.
 */
export function isProspect(session: ProspectSession): boolean | undefined {
  const { pathname, authorizing, loggedIn, user } = session;
  if (/^\/(support|accountant)(\/|$)/.test(pathname)) return false;
  if (session.supportLoggedIn) return false;
  if (authorizing) return undefined;
  if (!loggedIn) return true;
  if (session.impersonating) return false;
  return !!user && !user.plan;
}

/**
 * Whether this session should run the pixel: prospects only, never in local
 * development. `undefined` leaves the pixel as it is until auth settles.
 */
export function metaPixelWanted(
  session: MetaPixelSession
): boolean | undefined {
  if (session.hostname === "localhost") return false;
  return isProspect(session);
}

/**
 * Loads the pixel, or pauses it, to match `wanted` and the visitor's opt-out.
 * Pausing uses Meta's consent revoke, since a loaded pixel can't be unloaded.
 * @param wanted result of `metaPixelWanted`
 */
export function syncMetaPixel(wanted: boolean) {
  const w = window as any;
  if (!wanted || trackingOptedOut()) {
    w.fbq?.("consent", "revoke");
    return;
  }
  if (w.fbq) w.fbq("consent", "grant");
  else loadPixel();
}

// XXX keep in sync with `signUpEventId` / `purchaseEventId` in
// MRGBackendAPI/MRGFunctions/functions/src/meta.js. The backend sends the same
// events to the Meta Conversions API; matching ids make Meta count each once.
export const metaSignUpEventId = (uid: string) => `signup-${uid}`;
export const metaPurchaseEventId = (subscriptionId: string) =>
  `purchase-${subscriptionId}`;

export type MetaBrowserContext =
  | { optedOut: true }
  | { fbp?: string; fbc?: string; eventSourceUrl?: string };

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  const match = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(prefix));
  return match ? decodeURIComponent(match.slice(prefix.length)) : undefined;
}

/**
 * Sent with signUp / updateSubscription so the backend can report the same
 * conversion to Meta. When the visitor opted out (cookie bar or Global Privacy
 * Control) it tells the backend not to report this user.
 */
export function metaBrowserContext(): MetaBrowserContext {
  if (trackingOptedOut()) return { optedOut: true };
  return {
    fbp: readCookie("_fbp"),
    fbc: readCookie("_fbc"),
    eventSourceUrl: window.location.href,
  };
}
